import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useUserContext } from "../config/UserContext";
import type { Freeze } from "../../../shared/types/models/freeze";
import { useKeycloak } from "../config/KeycloakContext";
import { useEditor, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Image from '@tiptap/extension-image';
import Dropcursor from '@tiptap/extension-dropcursor';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import { DOCUMENT_TYPE } from '../../../shared/types/models/document';

interface PropertyInfoCardProps {
  property: Freeze;
  setProperty: (p: Freeze) => void;
  onContinue: () => void;
  documentType?: number;
}

const PropertyInfoCard: React.FC<PropertyInfoCardProps> = ({ property, setProperty, onContinue, documentType }) => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { state, county, apiBaseUrl, apiBaseUrlLoading, apiBaseUrlError } = useUserContext();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { getToken } = useKeycloak();
  // Feature flag for Tiptap
  const [enableTipTap, setEnableTipTap] = useState<boolean | null>(null);
  const DEFAULT_PROVIDE_TEXT = "Customer will provide their own copy.";
  const [legalDescMode, setLegalDescMode] = useState<'provide' | 'enter'>(
    property.legalDescription === DEFAULT_PROVIDE_TEXT ? 'provide' : 'enter'
  );

  const isLienRelease = documentType !== DOCUMENT_TYPE.LIEN;

  useEffect(() => {
    fetch('/config.json')
      .then(res => res.json())
      .then(cfg => setEnableTipTap(!!cfg.EnableTipTap))
      .catch(() => setEnableTipTap(false));
  }, []);

  // --- Browser storage for legal description ---
  useEffect(() => {
    // Only set if not already set (default to empty)
    if (!property.legalDescription || property.legalDescription === '' || property.legalDescription === 'null') {
      const savedLegalDescription = localStorage.getItem('propertyInfo.legalDescription');
      if (savedLegalDescription && savedLegalDescription !== 'null') {
        setProperty({ ...property, legalDescription: savedLegalDescription });
      }
    }
    // eslint-disable-next-line
  }, []);

  // Validation schema for the form (client-side only)
  const PropertyInfoSchema = z.object({
    street1: z.string().min(1, "Address is required"),
    city: z.string().min(1, "City is required"),
    zipcode: z.string().min(1, "Zip code is required").regex(/^[0-9]{5}(-[0-9]{4})?$/, "Please enter a valid US ZIP code (e.g., 12345 or 12345-6789)"),
    legalDescription: z.string().min(1, "Legal description is required"),
  });

  const validate = (data: Freeze) => {
    const result = PropertyInfoSchema.safeParse(data);
    if (result.success) {
      setErrors({});
      return true;
    } else {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) fieldErrors[err.path[0]] = err.message;
      });
      setErrors(fieldErrors);
      return false;
    }
  };

  const handleBlur = (field: keyof Freeze) => {
    setTouched(t => ({ ...t, [field]: true }));
    validate({ ...property });
  };

  const isFormValid = PropertyInfoSchema.safeParse({ ...property }).success;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ street1: true, city: true, county: true, state: true, zipcode: true, legalDescription: true });
    if (!PropertyInfoSchema.safeParse({ ...property }).success) {
      validate({ ...property });
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      const addressPayload = {
        street1: property.street1,
        street2: property.street2 ? property.street2 : "",
        city: property.city,
        stateId: state ? state.id : undefined,
        countyId: county ? county.id : undefined,
        zipcode: property.zipcode,
        country: 'US',
      };
      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      };
      const res = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${property.lookup}/address`, {
        method: "PUT",
        headers,
        body: JSON.stringify(addressPayload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to save property info");
      }
      // Now update legal description
      const legalDescriptionRes = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${property.lookup}/legalDescription`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ legalDescription: property.legalDescription }),
      });
      if (!legalDescriptionRes.ok) {
        const err = await legalDescriptionRes.json();
        throw new Error(err.message || "Failed to save legal description");
      }
      setSaving(false);
      onContinue();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setSaveError(err.message || "Failed to save property info");
      } else {
        setSaveError("Failed to save property info");
      }
      setSaving(false);
    }
  };

  // Tiptap editor setup for legal description
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      Image,
      Dropcursor,
      Bold,
      Italic,
    ],
    content: property.legalDescription || '',
    onUpdate: ({ editor }) => {
      setProperty({ ...property, legalDescription: editor.getHTML() });
      // localStorage update is handled by useEffect above
    },
    editorProps: {
      attributes: {
        id: 'property-legal-description',
        class: `w-full px-2 py-1.5 rounded border ${errors.legalDescription && touched.legalDescription ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none min-h-[150px]`,
        style: 'min-height: 150px;',
      },
    },
  });

  const handleModeChange = (mode: 'provide' | 'enter') => {
    setLegalDescMode(mode);
    if (mode === 'provide') {
      setProperty({ ...property, legalDescription: DEFAULT_PROVIDE_TEXT });
      editor?.commands.setContent(DEFAULT_PROVIDE_TEXT);
    } else if (property.legalDescription === DEFAULT_PROVIDE_TEXT) {
      setProperty({ ...property, legalDescription: '' });
      editor?.commands.setContent('');
    }
  };

  if (apiBaseUrlLoading) return <div>Loading configuration...</div>;
  if (apiBaseUrlError) return <div>Error loading configuration: {apiBaseUrlError}</div>;

  return (
    <>
      <form className="w-full flex flex-col gap-3" onSubmit={handleSubmit} autoComplete="off">
        {/* Address (full width) */}
        <div className="flex flex-col gap-1 w-full">
          <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta" htmlFor="property-address">Address</label>
          <input
            id="property-address"
            type="text"
            className={`w-full px-2 py-1.5 rounded border ${errors.street1 && touched.street1 ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none`}
            value={property.street1 || ''}
            onChange={e => setProperty({ ...property, street1: e.target.value })}
            onBlur={() => handleBlur('street1')}
            disabled={isLienRelease}
            autoComplete="off"
          />
          {errors.street1 && touched.street1 && <span className="text-red-500 text-xs mt-1">{errors.street1}</span>}
        </div>
        {/* Address 2 (optional) */}
        <div className="flex flex-col gap-1 w-full">
          <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta" htmlFor="property-address2">Address 2</label>
          <input
            id="property-address2"
            type="text"
            className={`w-full px-2 py-1.5 rounded border ${errors.street2 && touched.street2 ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none`}
            value={property.street2 || ''}
            onChange={e => setProperty({ ...property, street2: e.target.value })}
            onBlur={() => handleBlur('street2')}
            disabled={isLienRelease}
            autoComplete="off"
          />
        </div>
        {/* Row: City and Zip code */}
        <div className="flex flex-row gap-3 w-full">
          {/* City */}
          <div className="flex flex-col gap-1 w-1/2">
            <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta" htmlFor="property-city">City</label>
            <input
              id="property-city"
              type="text"
              className={`w-full px-2 py-1.5 rounded border ${errors.city && touched.city ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none`}
              value={property.city || ''}
              onChange={e => setProperty({ ...property, city: e.target.value })}
              onBlur={() => handleBlur('city')}
              disabled={isLienRelease}
              autoComplete="off"
            />
            {errors.city && touched.city && <span className="text-red-500 text-xs mt-1">{errors.city}</span>}
          </div>
          {/* Zip code */}
          <div className="flex flex-col gap-1 w-1/2">
            <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta" htmlFor="property-zip">Zip code</label>
            <input
              id="property-zip"
              type="text"
              className={`w-full px-2 py-1.5 rounded border ${errors.zipcode && touched.zipcode ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none`}
              value={property.zipcode || ''}
              onChange={e => setProperty({ ...property, zipcode: e.target.value })}
              onBlur={() => handleBlur('zipcode')}
              disabled={isLienRelease}
              autoComplete="off"
            />
            {errors.zipcode && touched.zipcode && <span className="text-red-500 text-xs mt-1">{errors.zipcode}</span>}
          </div>
        </div>
        {/* Row: County and State */}
        <div className="flex flex-row gap-3 w-full">
          {/* County (disabled input) */}
          <div className="flex flex-col gap-1 w-1/2">
            <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta" htmlFor="property-county">County</label>
            <input
              id="property-county"
              type="text"
              className={`w-full px-2 py-1.5 rounded border ${errors.county && touched.county ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none`}
              value={(property as any).county ? (property as any).county.name : county ? county.name : ''}
              disabled
              autoComplete="off"
            />
          </div>
          {/* State (disabled input) */}
          <div className="flex flex-col gap-1 w-1/2">
            <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta" htmlFor="property-state">State</label>
            <input
              id="property-state"
              type="text"
              className={`w-full px-2 py-1.5 rounded border ${errors.state && touched.state ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none`}
              value={(property as any).state ? (property as any).state.name : state ? state.name : ''}
              disabled
              autoComplete="off"
            />
          </div>
        </div>
        {/* Legal Description */}
        <div className="flex flex-col gap-1 w-full">
          <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta" htmlFor="property-legal-description">Legal Description</label>
          <div className="flex flex-row gap-6 mb-2 items-center">
            <label className="flex items-center gap-2 cursor-pointer text-[#141435] text-[15px] font-plus-jakarta">
              <input
                type="radio"
                name="legalDescMode"
                value="provide"
                checked={legalDescMode === 'provide'}
                onChange={() => handleModeChange('provide')}
                className="w-4 h-4 text-[#0B4BB6] focus:ring-[#0B4BB6]"
              />
              Provide my own
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-[#141435] text-[15px] font-plus-jakarta">
              <input
                type="radio"
                name="legalDescMode"
                value="enter"
                checked={legalDescMode === 'enter'}
                onChange={() => handleModeChange('enter')}
                className="w-4 h-4 text-[#0B4BB6] focus:ring-[#0B4BB6]"
              />
              Enter it
            </label>
          </div>
          {enableTipTap === null ? (
            <div>Loading editor...</div>
          ) : enableTipTap ? (
            <div className={`transition-opacity duration-200 ${legalDescMode === 'provide' ? 'opacity-50 pointer-events-none' : ''}`}>
              {/* Tiptap Toolbar */}
              {editor && (
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    className={`px-2 py-1 rounded border ${editor.isActive('bold') ? 'bg-[#0B4BB6] text-white' : 'bg-white text-[#141435] border-[#D7DFEC]'} font-plus-jakarta text-sm`}
                    onClick={() => editor.chain().focus().setMark('bold').run()}
                    aria-label="Bold"
                  >
                    <b>B</b>
                  </button>
                  <button
                    type="button"
                    className={`px-2 py-1 rounded border ${editor.isActive('italic') ? 'bg-[#0B4BB6] text-white' : 'bg-white text-[#141435] border-[#D7DFEC]'} font-plus-jakarta text-sm italic`}
                    onClick={() => editor.chain().focus().setMark('italic').run()}
                    aria-label="Italic"
                  >
                    I
                  </button>
                  <button
                    type="button"
                    className="px-2 py-1 rounded border bg-white text-[#141435] border-[#D7DFEC] font-plus-jakarta text-sm"
                    onClick={() => {
                      const url = window.prompt('Enter image URL');
                      if (url) editor.chain().focus().setImage({ src: url }).run();
                    }}
                    aria-label="Insert Image"
                  >
                    🖼️
                  </button>
                </div>
              )}
              <EditorContent
                editor={editor}
                onBlur={() => handleBlur('legalDescription')}
              />
            </div>
          ) : (
            <textarea
              id="property-legal-description"
              className={`w-full px-2 py-1.5 rounded border ${errors.legalDescription && touched.legalDescription ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} ${legalDescMode === 'provide' ? 'opacity-50 pointer-events-none' : ''} text-[15px] font-plus-jakarta focus:outline-none min-h-[150px] transition-opacity duration-200`}
              value={property.legalDescription || ''}
              onChange={e => setProperty({ ...property, legalDescription: e.target.value })}
              onBlur={() => handleBlur('legalDescription')}
              rows={6}
              disabled={isLienRelease || legalDescMode === 'provide'}
              autoComplete="off"
            />
          )}
          {errors.legalDescription && touched.legalDescription && <span className="text-red-500 text-xs mt-1">{errors.legalDescription}</span>}
        </div>
        <button
          type="submit"
          className={`w-full bg-[#0B4BB6] text-white font-bold text-[15px] font-plus-jakarta py-2.5 rounded-full mt-4 hover:bg-[#23235a] transition-colors border-2 border-[#0B4BB6] ${((!isFormValid || saving) && !isLienRelease) || (isLienRelease && saving) ? 'opacity-60 cursor-not-allowed' : ''}`}
          disabled={isLienRelease ? saving : (!isFormValid || saving)}
          aria-disabled={isLienRelease ? saving : (!isFormValid || saving)}
          data-testid="propertyinfo-continue-button"
        >
          {isLienRelease ? 'Continue' : saving ? 'Saving...' : 'Save & Continue'}
        </button>
        {saveError && <div className="text-red-500 mt-2">{saveError}</div>}
      </form>
    </>
  );
};

export default PropertyInfoCard;
